import { useEffect, useMemo, useState } from 'react';
import { Text } from '@belong/ui';
import { IconDots } from '@belong/icons';
import type {
  ConsumerPaymentMethodSummaryModel,
  ConsumerPaymentRescheduleModel,
  ConsumerPaymentRescheduleTotalChargeModel,
  ReschedulePaymentPlan,
} from '../failed-payments.types';
import type { PaymentAccountResourceModel } from 'api/models';
import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import { PAYMENT_SCREENS } from 'containercomponents/Modals/PaymentsModal/PaymentsModal';
import PaymentMenuSelectionContainer from 'containers/PaymentSelectionMenuContainer/PaymentMenuSelectionContainer';
import { PaymentPreference } from 'models/enums';
import { FEEDBACK_STRINGS } from 'strings/errors.string';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';
import {
  formatFailedPaymentsAmount,
  formatLateFee,
  formatNegativeFailedPaymentsAmountWithCents,
  mapPaymentAccountToPaymentMethodSummary,
} from '../failed-payments-utils';
import { PaymentMethodSummary } from '../payment-method-summary';
import { getFeeLabel } from 'pages/Accounts/Pages/YourHome/Rent/PayNow/pay-now-utils';

const DAYS_AHEAD = 6;

type ApiClient = {
  post: <T>(url: string, body: unknown) => Promise<T>;
};

type RemainingPaymentBalance = {
  balanceUniqueId: string;
  amount: number;
};

type UnableToPaySubsectionProps = {
  apiClient: ApiClient;
  paymentMethods: PaymentAccountResourceModel[];
  paymentAccountUniqueId: string | null;
  paymentMethod: ConsumerPaymentMethodSummaryModel | null | undefined;
  selectedPlan: ReschedulePaymentPlan | null;
  partialPaymentAmount: string;
  creditCardFeeAmount?: number;
  remainingCreditCardFeeAmount?: number;
  isPartialPaymentAmountInvalid: boolean;
  isPartialPaymentPreviewLoading: boolean;
  partialPaymentPreviewError: boolean;
  remainingPaymentDate: string | null;
  remainingPaymentBalances: RemainingPaymentBalance[];
  onSelectPlan: (plan: ReschedulePaymentPlan) => void;
  onChangePartialPaymentAmount: (amount: string) => void;
  onSelectRemainingPaymentDate: (date: string) => void;
  onSelectPaymentMethod: (id: string) => void;
  onLateFeeErrorChange: (hasError: boolean) => void;
  onRetryPartialPaymentPreview: () => void;
};

const PROCEED_OPTIONS: {
  value: ReschedulePaymentPlan;
  labelKey: keyof typeof FAILED_PAYMENTS_STRINGS;
}[] = [
  {
    value: 'ScheduledPayments',
    labelKey: 'unable_to_pay.proceed.partial_payment',
  },
  {
    value: 'UnableToPayIndefinitely',
    labelKey: 'unable_to_pay.proceed.unable_anymore',
  },
];

function PaymentSummaryRow({
  label,
  amount,
  emphasized = false,
}: {
  label: string;
  amount: number;
  emphasized?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-md">
      <Text
        variant="body"
        fontWeight={emphasized ? 'semibold' : 'regular'}
        className="text-navy"
      >
        {label}
      </Text>
      <Text
        variant="body"
        fontWeight={emphasized ? 'semibold' : 'regular'}
        className="shrink-0 text-right text-navy"
      >
        {formatNegativeFailedPaymentsAmountWithCents(amount)}
      </Text>
    </div>
  );
}

export function UnableToPaySubsection({
  apiClient,
  paymentMethods,
  paymentAccountUniqueId,
  paymentMethod,
  selectedPlan,
  partialPaymentAmount,
  creditCardFeeAmount,
  remainingCreditCardFeeAmount,
  isPartialPaymentAmountInvalid,
  isPartialPaymentPreviewLoading,
  partialPaymentPreviewError,
  remainingPaymentDate,
  remainingPaymentBalances,
  onSelectPlan,
  onChangePartialPaymentAmount,
  onSelectRemainingPaymentDate,
  onSelectPaymentMethod,
  onLateFeeErrorChange,
  onRetryPartialPaymentPreview,
}: UnableToPaySubsectionProps) {
  const isPartialPayment = selectedPlan === 'ScheduledPayments';
  const isUnableAnymore = selectedPlan === 'UnableToPayIndefinitely';
  const availablePaymentMethods = paymentMethods.filter(
    (method) => method.accountId && method.isVerified,
  );
  const selectedPaymentMethod =
    availablePaymentMethods.find(
      (method) => method.accountId === paymentAccountUniqueId,
    ) ?? availablePaymentMethods[0];
  const selectedPaymentMethodSummary = selectedPaymentMethod
    ? mapPaymentAccountToPaymentMethodSummary(selectedPaymentMethod)
    : paymentMethod;
  const dates = useMemo(() => {
    const arr: { iso: string; label: string }[] = [];
    const base = new Date();
    for (let i = 1; i <= DAYS_AHEAD; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      arr.push({
        iso: format(d, 'yyyy-MM-dd'),
        label: format(d, 'EEE, MMM d, yyyy'),
      });
    }
    return arr;
  }, []);
  const [dateFees, setDateFees] = useState<Record<string, number>>({});
  const [lateFeeError, setLateFeeError] = useState(false);
  const datesKey = dates.map((d) => d.iso).join(',');
  const remainingBalancesKey = remainingPaymentBalances
    .map((balance) => `${balance.balanceUniqueId}:${balance.amount}`)
    .join(',');

  useEffect(() => {
    if (!paymentAccountUniqueId && selectedPaymentMethod?.accountId) {
      onSelectPaymentMethod(selectedPaymentMethod.accountId);
    }
  }, [
    onSelectPaymentMethod,
    paymentAccountUniqueId,
    selectedPaymentMethod?.accountId,
  ]);

  useEffect(() => {
    if (
      !isPartialPayment ||
      !paymentAccountUniqueId ||
      remainingPaymentBalances.length === 0
    ) {
      setDateFees({});
      setLateFeeError(false);
      onLateFeeErrorChange(false);
      return;
    }

    let cancelled = false;

    (async () => {
      const results = await Promise.all(
        dates.map(async ({ iso }) => {
          try {
            const totals: ConsumerPaymentRescheduleTotalChargeModel[] =
              await Promise.all(
                remainingPaymentBalances.map((balance) =>
                  apiClient.post<ConsumerPaymentRescheduleTotalChargeModel>(
                    `/v2/users/balances/${balance.balanceUniqueId}/payment-reschedule/total`,
                    {
                      retryStrategy: 'UnableToPay',
                      paymentPlan: 'ScheduledPayments',
                      schedulePlans: [
                        {
                          processOn: iso,
                          processAmount: balance.amount,
                          paymentAccountUniqueId,
                        },
                      ],
                    } as ConsumerPaymentRescheduleModel,
                  ),
                ),
              );
            const lateFee = totals.reduce(
              (sum, total) => sum + (total?.lateFeeCharge ?? 0),
              0,
            );
            return { iso, lateFee, hasError: false };
          } catch {
            return { iso, lateFee: 0, hasError: true };
          }
        }),
      );

      if (!cancelled) {
        const hasError = results.some((result) => result.hasError);
        const entries = results.map(
          ({ iso, lateFee }) => [iso, lateFee] as const,
        );
        setDateFees(Object.fromEntries(entries));
        setLateFeeError(hasError);
        onLateFeeErrorChange(hasError);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    apiClient,
    datesKey,
    isPartialPayment,
    paymentAccountUniqueId,
    remainingBalancesKey,
    onLateFeeErrorChange,
  ]);

  const paymentModalProps = {
    screen: PAYMENT_SCREENS.PAYMENT_SELECTION,
    errorScreenProps: {
      title: FEEDBACK_STRINGS.set_payment_method.error.title,
      ctaLabel: FEEDBACK_STRINGS.set_payment_method.error.ctaLabel,
    },
    successScreenProps: {
      title: FEEDBACK_STRINGS.set_payment_method.success.title,
      ctaLabel: FEEDBACK_STRINGS.set_payment_method.success.ctaLabel,
    },
  };

  function handleAmountChange(value: string) {
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      onChangePartialPaymentAmount(value);
    }
  }

  const selectedFee = remainingPaymentDate
    ? (dateFees[remainingPaymentDate] ?? 0)
    : 0;
  const formattedSelectedFee = formatLateFee(selectedFee);
  const remainingPaymentTotal = remainingPaymentBalances.reduce(
    (sum, balance) => sum + balance.amount,
    0,
  );
  // Payment Summary breakdown (Figma 3109:2443): the entered amount + its card fee charge today,
  // the scheduled remainder + late fee + its card fee charge on the chosen date.
  const immediatePayment = Number(partialPaymentAmount) || 0;
  const creditCardFeeToday = creditCardFeeAmount ?? 0;
  const chargingTodayTotal = immediatePayment + creditCardFeeToday;
  const creditCardFeeRemaining = remainingCreditCardFeeAmount ?? 0;
  const chargingOnTotal =
    remainingPaymentTotal + selectedFee + creditCardFeeRemaining;
  const summaryTotal = chargingTodayTotal + chargingOnTotal;
  const hasEnteredPartialAmount = Number(partialPaymentAmount) > 0;
  const shouldShowPartialPaymentNextSteps =
    hasEnteredPartialAmount &&
    !isPartialPaymentAmountInvalid &&
    !isPartialPaymentPreviewLoading &&
    !partialPaymentPreviewError &&
    remainingPaymentBalances.length > 0;
  const amountInput = (
    <div className="flex flex-col gap-md">
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          value={partialPaymentAmount}
          onChange={(event) => handleAmountChange(event.target.value)}
          placeholder={
            FAILED_PAYMENTS_STRINGS['unable_to_pay.partial.amount_placeholder']
          }
          className={clsx(
            'h-button w-full border-0 border-b border-solid border-gray bg-transparent text-body text-navy outline-none placeholder:text-dark-gray',
            partialPaymentAmount && 'pl-[10px]',
          )}
        />
        {partialPaymentAmount ? (
          <span className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 text-body text-navy">
            $
          </span>
        ) : null}
      </div>
      {isPartialPaymentAmountInvalid ? (
        <Text variant="p2" fontWeight="semibold" className="text-red">
          {FAILED_PAYMENTS_STRINGS['error.amount_too_high']}
        </Text>
      ) : null}
      {isPartialPaymentPreviewLoading &&
      hasEnteredPartialAmount &&
      !isPartialPaymentAmountInvalid ? (
        <Text variant="p2" className="text-dark-gray">
          {FAILED_PAYMENTS_STRINGS['unable_to_pay.partial.preview_loading']}
        </Text>
      ) : null}
      {partialPaymentPreviewError &&
      hasEnteredPartialAmount &&
      !isPartialPaymentAmountInvalid ? (
        <div className="flex flex-col gap-xs rounded border border-solid border-red bg-[rgba(255,86,97,0.04)] p-md">
          <Text variant="p2" className="text-navy">
            {FAILED_PAYMENTS_STRINGS['error.partial_payment_preview']}
          </Text>
          <button
            type="button"
            onClick={onRetryPartialPaymentPreview}
            className="self-start border-0 bg-transparent p-0 text-p2 font-semibold text-red underline"
          >
            {FAILED_PAYMENTS_STRINGS['error.partial_payment_preview_retry']}
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-md">
        <Text
          variant="h3"
          fontWeight="semibold"
          className="whitespace-pre-line"
        >
          {FAILED_PAYMENTS_STRINGS['unable_to_pay.proceed.prompt']}
        </Text>
        <div className="grid grid-cols-1 gap-sm sm:grid-cols-2 md:grid-cols-3 md:gap-md">
          {PROCEED_OPTIONS.map((option) => {
            const isSelected = selectedPlan === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => onSelectPlan(option.value)}
                className={clsx(
                  'flex h-[80px] cursor-pointer items-center justify-center rounded border-0 px-md text-center shadow transition-colors md:h-[110px]',
                  isSelected ? 'bg-navy' : 'bg-white',
                )}
              >
                <Text
                  variant="body"
                  fontWeight={isSelected ? 'semibold' : 'regular'}
                  className={isSelected ? 'text-white' : 'text-navy'}
                >
                  {FAILED_PAYMENTS_STRINGS[option.labelKey]}
                </Text>
              </button>
            );
          })}
        </div>
      </div>

      {isPartialPayment && (
        <>
          <div className="flex flex-col gap-md">
            <Text variant="h3" fontWeight="semibold">
              {FAILED_PAYMENTS_STRINGS['unable_to_pay.partial.amount_prompt']}
            </Text>
            {amountInput}
          </div>

          {shouldShowPartialPaymentNextSteps && (
            <>
              <div className="flex flex-col gap-md">
                <Text variant="h3" fontWeight="semibold">
                  {
                    FAILED_PAYMENTS_STRINGS[
                      'unable_to_pay.partial.remaining_date_prompt'
                    ]
                  }
                </Text>

                <div className="grid grid-cols-1 gap-md sm:grid-cols-2 md:grid-cols-3">
                  {dates.map(({ iso, label }) => {
                    const isSelected = remainingPaymentDate === iso;
                    const fee = dateFees[iso] ?? 0;
                    const formattedFee = formatLateFee(fee);

                    return (
                      <button
                        key={iso}
                        type="button"
                        onClick={() => onSelectRemainingPaymentDate(iso)}
                        className={clsx(
                          'flex h-[64px] w-full cursor-pointer flex-col items-center justify-center rounded border-0 px-sm shadow transition-colors',
                          isSelected ? 'bg-navy' : 'bg-white',
                        )}
                      >
                        <Text
                          variant="body"
                          fontWeight={isSelected ? 'semibold' : 'regular'}
                          className={isSelected ? 'text-white' : 'text-navy'}
                        >
                          {label}
                        </Text>
                        {fee > 0 && (
                          <Text
                            variant="p2"
                            fontWeight="semibold"
                            className="text-dark-gray"
                          >
                            + {formattedFee}{' '}
                            {
                              FAILED_PAYMENTS_STRINGS[
                                'schedule_later.late_fee_label'
                              ]
                            }
                          </Text>
                        )}
                      </button>
                    );
                  })}
                </div>

                {lateFeeError && (
                  <div className="flex flex-col gap-xs rounded border border-solid border-red bg-[rgba(255,86,97,0.04)] p-md">
                    <Text variant="p2" className="text-navy">
                      {FAILED_PAYMENTS_STRINGS['error.late_fee_preview']}
                    </Text>
                  </div>
                )}

                {selectedFee > 0 && (
                  <div className="flex flex-col gap-xs rounded border border-solid border-green bg-green-translucent-light p-md">
                    <Text fontWeight="semibold" className="text-navy">
                      ⚠️ {formattedSelectedFee}{' '}
                      {FAILED_PAYMENTS_STRINGS['schedule_later.late_fee_label']}
                    </Text>
                    <Text variant="p1" className="text-navy">
                      {
                        FAILED_PAYMENTS_STRINGS[
                          'schedule_later.late_fee_description'
                        ]
                      }
                    </Text>
                  </div>
                )}
              </div>

              {selectedPaymentMethodSummary && (
                <div className="w-full">
                  <PaymentMenuSelectionContainer
                    selectedPaymentMethodId={paymentAccountUniqueId ?? ''}
                    onSelectPaymentMethod={onSelectPaymentMethod}
                    paymentType={PaymentPreference.Rent}
                    paymentModalProps={paymentModalProps}
                    showOnlyValidPaymentMethods
                    shouldShowAutoSplitPaymentMethod={false}
                    onSelectAutoSplitPaymentMethod={() => {}}
                    fitToButton
                    onTop
                    menuButtonLabel={
                      <span className="flex h-[106px] w-full items-center rounded border border-solid border-gray bg-white px-[17px] py-[34px] text-left">
                        <span className="min-w-0 flex-1">
                          <PaymentMethodSummary
                            paymentMethod={selectedPaymentMethodSummary}
                          />
                        </span>
                        <span className="inline-flex px-xs text-navy">
                          <IconDots width={5} height={24} />
                        </span>
                      </span>
                    }
                  />
                </div>
              )}

              {remainingPaymentDate && (
                <div className="flex w-full flex-col gap-md">
                  <Text variant="h3" fontWeight="semibold">
                    {
                      FAILED_PAYMENTS_STRINGS[
                        'unable_to_pay.partial.summary_title'
                      ]
                    }
                  </Text>

                  <div className="flex flex-col gap-xl">
                    <div className="flex flex-col gap-sm">
                      <PaymentSummaryRow
                        label={
                          FAILED_PAYMENTS_STRINGS[
                            'unable_to_pay.partial.immediate_payment'
                          ]
                        }
                        amount={immediatePayment}
                      />
                      {creditCardFeeToday > 0 ? (
                        <PaymentSummaryRow
                          label={getFeeLabel('CreditCard')}
                          amount={creditCardFeeToday}
                        />
                      ) : null}
                      <PaymentSummaryRow
                        label={
                          FAILED_PAYMENTS_STRINGS[
                            'unable_to_pay.partial.charging_today'
                          ]
                        }
                        amount={chargingTodayTotal}
                        emphasized
                      />
                    </div>

                    <div className="border-0 border-t border-dashed border-gray" />

                    <div className="flex flex-col gap-sm">
                      <PaymentSummaryRow
                        label={
                          FAILED_PAYMENTS_STRINGS[
                            'unable_to_pay.partial.remaining_balance'
                          ]
                        }
                        amount={remainingPaymentTotal}
                      />
                      {selectedFee > 0 ? (
                        <PaymentSummaryRow
                          label={
                            FAILED_PAYMENTS_STRINGS[
                              'schedule_later.late_fee_label'
                            ]
                          }
                          amount={selectedFee}
                        />
                      ) : null}
                      {creditCardFeeRemaining > 0 ? (
                        <PaymentSummaryRow
                          label={getFeeLabel('CreditCard')}
                          amount={creditCardFeeRemaining}
                        />
                      ) : null}
                      <PaymentSummaryRow
                        label={`${
                          FAILED_PAYMENTS_STRINGS[
                            'unable_to_pay.partial.charging_on'
                          ]
                        } ${format(parseISO(remainingPaymentDate), 'MMM d, yyyy')}`}
                        amount={chargingOnTotal}
                        emphasized
                      />
                    </div>

                    <div className="border-0 border-t border-dashed border-gray" />

                    <div className="flex items-start justify-between gap-md">
                      <Text variant="h3" fontWeight="semibold">
                        {
                          FAILED_PAYMENTS_STRINGS[
                            'unable_to_pay.partial.total_amount'
                          ]
                        }
                      </Text>
                      <div className="flex shrink-0 flex-col items-end gap-2xs">
                        <Text
                          variant="h3"
                          fontWeight="semibold"
                          className="text-right"
                        >
                          {formatNegativeFailedPaymentsAmountWithCents(
                            summaryTotal,
                          )}
                        </Text>
                        <Text
                          variant="p2"
                          className="text-right text-dark-gray"
                        >
                          {FAILED_PAYMENTS_STRINGS[
                            'unable_to_pay.partial.pay_now_later'
                          ]
                            .replace(
                              '{now}',
                              formatFailedPaymentsAmount(chargingTodayTotal),
                            )
                            .replace(
                              '{later}',
                              formatFailedPaymentsAmount(chargingOnTotal),
                            )}
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {isUnableAnymore && (
        <div className="rounded border border-solid border-red bg-[rgba(255,86,97,0.04)] p-md">
          <Text variant="body" className="text-navy">
            {FAILED_PAYMENTS_STRINGS['unable_to_pay.indefinite.callout']}
          </Text>
        </div>
      )}
    </div>
  );
}
